const normalizeGroupId = (value) => {
    if (value === undefined || value === null) {
        return null;
    }
    const normalized = String(value).trim();
    return normalized || null;
};

const normalizeBandName = (value) => {
    if (typeof value !== "string") {
        return null;
    }
    const normalized = value.trim().toLowerCase();
    return normalized || null;
};

const normalizeBandObjectId = (value) => {
    if (typeof value !== "string") {
        return null;
    }
    const normalized = value.trim().toLowerCase();
    return normalized || null;
};

const normalizeExistingGroupIds = (existingGroupIds) => {
    if (!existingGroupIds) {
        return new Set();
    }
    const values = existingGroupIds instanceof Set
        ? Array.from(existingGroupIds)
        : Array.isArray(existingGroupIds)
            ? existingGroupIds
            : [];
    return new Set(
        values
            .map(normalizeGroupId)
            .filter(Boolean)
    );
};

export const getLinkedBandWorkspacePollDelay = (attemptNumber) => {
    const normalizedAttempt = Number.isFinite(attemptNumber) ? Math.max(1, Math.floor(attemptNumber)) : 1;
    return normalizedAttempt <= 12 ? 5000 : 30000;
};

export const findLinkedBandWorkspace = (groups, { existingGroupIds, bandObjectId, bandName } = {}) => {
    const previousIds = normalizeExistingGroupIds(existingGroupIds);
    const nextGroups = Array.isArray(groups) ? groups : [];
    const newGroups = nextGroups.filter((group) => {
        const normalizedId = normalizeGroupId(group?.id);
        return normalizedId && !previousIds.has(normalizedId);
    });

    const normalizedBandObjectId = normalizeBandObjectId(bandObjectId);
    if (normalizedBandObjectId) {
        const bandMatches = newGroups.filter(
            (group) => normalizeBandObjectId(group?.linkedBandObjectId) === normalizedBandObjectId
        );
        if (bandMatches.length === 1) {
            return bandMatches[0];
        }
        return null;
    }

    const normalizedBandName = normalizeBandName(bandName);
    if (!normalizedBandName) {
        return null;
    }

    const nameMatches = newGroups.filter(
        (group) => normalizeBandName(group?.name) === normalizedBandName
    );

    return nameMatches.length === 1 ? nameMatches[0] : null;
};

const defaultSleep = (delayMs) => new Promise((resolve) => {
    setTimeout(resolve, delayMs);
});

export const waitForLinkedBandWorkspace = async ({
    fetchGroups,
    existingGroupIds,
    bandObjectId,
    bandName,
    maxAttempts = 6,
    delayMs = 750,
    sleep = defaultSleep,
} = {}) => {
    if (typeof fetchGroups !== "function") {
        throw new Error("fetchGroups is required");
    }

    let latestGroups = [];
    const safeAttempts = Math.max(1, Number.isFinite(maxAttempts) ? Math.floor(maxAttempts) : 1);

    for (let attempt = 0; attempt < safeAttempts; attempt += 1) {
        const snapshot = await fetchGroups();
        latestGroups = Array.isArray(snapshot) ? snapshot : [];

        const matchedGroup = findLinkedBandWorkspace(latestGroups, {
            existingGroupIds,
            bandObjectId,
            bandName,
        });

        if (matchedGroup) {
            return {
                groups: latestGroups,
                matchedGroup,
                isProvisioning: false,
            };
        }

        if (attempt < safeAttempts - 1) {
            await sleep(delayMs);
        }
    }

    return {
        groups: latestGroups,
        matchedGroup: null,
        isProvisioning: true,
    };
};
